import { useEffect } from "react";

export interface MetaTagConfig {
  lang?: string;
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCard?: string;
  canonical?: string;
  localizedMeta?: LocalizedMetaTag[];
}

interface LocalizedMetaTag {
  lang: string;
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
}

const defaultMetaTags: MetaTagConfig = {
  lang: "en",
  title: "US2TJK – Your Global Shopping Assistant",
  description:
    "Shop from Amazon, Walmart, and more. Let US2TJK handle the international shipping to your country. Fast, reliable cargo service.",
  keywords:
    "Amazon shipping, Walmart delivery, Tajikistan cargo, US to TJ shipping, shop and ship, US2TJK",
  ogTitle: "US2TJK – Shop USA. Deliver Anywhere.",
  ogDescription:
    "Shop from your favorite US stores and let us ship it to you. Reliable delivery and customer support.",
  ogImage: "/assets/og-image.png",
  ogUrl: "https://us2tjk.com",
  ogType: "website",
  twitterTitle: "US2TJK",
  twitterDescription:
    "Shop from US stores and get it delivered internationally.",
  twitterImage: "/assets/og-image.png",
  twitterCard: "summary_large_image",
  localizedMeta: [
    {
      lang: "ru",
      title: "US2TJK – ваш помощник в покупках по всему миру",
      description:
        "Покупайте в Amazon, Walmart и других магазинах США. US2TJK быстро и надежно доставит товары в вашу страну.",
      keywords:
        "доставка Amazon, доставка Walmart, груз в Таджикистан, доставка из США в Таджикистан, покупки и доставка, US2TJK",
      ogTitle: "US2TJK – Покупайте в США, доставим куда угодно",
      ogDescription:
        "Покупайте в любимых магазинах США, а мы доставим. Надежная доставка и поддержка клиентов.",
    },
    {
      lang: "tg",
      title: "US2TJK – Ёвари хариди ҷаҳонии шумо",
      description:
        "Аз Amazon, Walmart ва дигар мағозаҳои ИМА харид кунед. US2TJK борро зуд ва боэътимод ба кишвари шумо мерасонад.",
      keywords:
        "расонидани Amazon, расонидани Walmart, бор ба Тоҷикистон, аз ИМА ба Тоҷикистон, хариду расонидан, US2TJK",
      ogTitle: "US2TJK – Аз ИМА харед, мо мерасонем",
      ogDescription:
        "Аз мағозаҳои дӯстдоштаатон дар ИМА харид кунед, мо мерасонем. Расониши боэътимод ва дастгирии муштариён.",
    },
  ],
};

/**
 * Custom hook for managing dynamic meta tags
 * @param config - Meta tag configuration object
 */
export const useMetaTags = (config: MetaTagConfig = {}) => {
  const metaConfig = { ...defaultMetaTags, ...config };
  const localizedMeta = metaConfig.localizedMeta || [];
  const defaultLocalizedMeta = defaultMetaTags.localizedMeta || [];

  useEffect(() => {
    if (metaConfig.lang) {
      document.documentElement.lang = metaConfig.lang;
    }

    // Update document title
    if (metaConfig.title) {
      document.title = metaConfig.title;
    }

    // Update or create meta tags
    const updateMetaTag = (
      name: string,
      content: string,
      options: { property?: boolean; lang?: string } = {},
    ) => {
      const attribute = options.property ? "property" : "name";
      const langSelector = options.lang ? `[lang="${options.lang}"]` : "";
      let meta = document.querySelector(
        `meta[${attribute}="${name}"]${langSelector}`,
      ) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, name);
        if (options.lang) {
          meta.setAttribute("lang", options.lang);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", content);
    };

    // Basic meta tags
    if (metaConfig.description) {
      updateMetaTag("description", metaConfig.description);
    }
    if (metaConfig.keywords) {
      updateMetaTag("keywords", metaConfig.keywords);
    }
    localizedMeta.forEach((loc) => {
      if (loc.description) {
        updateMetaTag("description", loc.description, { lang: loc.lang });
      }
      if (loc.keywords) {
        updateMetaTag("keywords", loc.keywords, { lang: loc.lang });
      }
    });

    // Open Graph tags
    if (metaConfig.ogTitle) {
      updateMetaTag("og:title", metaConfig.ogTitle, { property: true });
    }
    if (metaConfig.ogDescription) {
      updateMetaTag("og:description", metaConfig.ogDescription, {
        property: true,
      });
    }
    if (metaConfig.ogImage) {
      updateMetaTag("og:image", metaConfig.ogImage, { property: true });
    }
    if (metaConfig.ogUrl) {
      updateMetaTag("og:url", metaConfig.ogUrl, { property: true });
    }
    if (metaConfig.ogType) {
      updateMetaTag("og:type", metaConfig.ogType, { property: true });
    }
    localizedMeta.forEach((loc) => {
      if (loc.ogTitle) {
        updateMetaTag("og:title", loc.ogTitle, {
          property: true,
          lang: loc.lang,
        });
      }
      if (loc.ogDescription) {
        updateMetaTag("og:description", loc.ogDescription, {
          property: true,
          lang: loc.lang,
        });
      }
    });

    // Twitter Card tags
    if (metaConfig.twitterCard) {
      updateMetaTag("twitter:card", metaConfig.twitterCard);
    }
    if (metaConfig.twitterTitle) {
      updateMetaTag("twitter:title", metaConfig.twitterTitle);
    }
    if (metaConfig.twitterDescription) {
      updateMetaTag("twitter:description", metaConfig.twitterDescription);
    }
    if (metaConfig.twitterImage) {
      updateMetaTag("twitter:image", metaConfig.twitterImage);
    }

    // Canonical URL
    if (metaConfig.canonical) {
      let canonical = document.querySelector(
        'link[rel="canonical"]',
      ) as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", metaConfig.canonical);
    }

    // Cleanup function to reset to defaults when component unmounts
    return () => {
      if (defaultMetaTags.lang) {
        document.documentElement.lang = defaultMetaTags.lang;
      }
      document.title = defaultMetaTags.title || "US2TJK";
      // Reset meta tags to defaults
      updateMetaTag("description", defaultMetaTags.description || "");
      updateMetaTag("keywords", defaultMetaTags.keywords || "");
      defaultLocalizedMeta.forEach((loc) => {
        if (loc.description) {
          updateMetaTag("description", loc.description, { lang: loc.lang });
        }
        if (loc.keywords) {
          updateMetaTag("keywords", loc.keywords, { lang: loc.lang });
        }
      });
      updateMetaTag("og:title", defaultMetaTags.ogTitle || "", {
        property: true,
      });
      updateMetaTag("og:description", defaultMetaTags.ogDescription || "", {
        property: true,
      });
      updateMetaTag("og:image", defaultMetaTags.ogImage || "", {
        property: true,
      });
      updateMetaTag("og:url", defaultMetaTags.ogUrl || "", { property: true });
      updateMetaTag("og:type", defaultMetaTags.ogType || "", { property: true });
      defaultLocalizedMeta.forEach((loc) => {
        if (loc.ogTitle) {
          updateMetaTag("og:title", loc.ogTitle, {
            property: true,
            lang: loc.lang,
          });
        }
        if (loc.ogDescription) {
          updateMetaTag("og:description", loc.ogDescription, {
            property: true,
            lang: loc.lang,
          });
        }
      });
      updateMetaTag("twitter:card", defaultMetaTags.twitterCard || "");
      updateMetaTag("twitter:title", defaultMetaTags.twitterTitle || "");
      updateMetaTag(
        "twitter:description",
        defaultMetaTags.twitterDescription || "",
      );
      updateMetaTag("twitter:image", defaultMetaTags.twitterImage || "");
    };
  }, [metaConfig]);
};

/**
 * Utility function to generate meta tags for specific pages
 */
export const generatePageMetaTags = {
  home: (): MetaTagConfig => ({
    title: "US2TJK – Your Global Shopping Assistant",
    description:
      "Shop from Amazon, Walmart, and more. Let US2TJK handle the international shipping to your country. Fast, reliable cargo service.",
    keywords:
      "Amazon shipping, Walmart delivery, Tajikistan cargo, US to TJ shipping, shop and ship, US2TJK",
    canonical: "https://us2tjk.com/",
  }),

  search: (query?: string): MetaTagConfig => ({
    title: query
      ? `Search Results for "${query}" - US2TJK`
      : "Search Products - US2TJK",
    description: query
      ? `Find the best deals on "${query}" from Amazon, Walmart, and other US stores. Get it shipped to your country with US2TJK.`
      : "Search millions of products from US stores. Find what you need and get it delivered internationally.",
    keywords: query
      ? `${query}, Amazon, Walmart, US shopping, international shipping, US2TJK`
      : "product search, Amazon search, Walmart search, US stores, international shipping, US2TJK",
    canonical: query
      ? `https://us2tjk.com/search?q=${encodeURIComponent(query)}`
      : "https://us2tjk.com/search",
  }),

  product: (productName?: string, productId?: string): MetaTagConfig => ({
    title: productName
      ? `${productName} - US2TJK`
      : "Product Details - US2TJK",
    description: productName
      ? `Buy ${productName} from US stores and get it shipped to your country. Fast, reliable delivery with US2TJK.`
      : "View product details and get it shipped internationally from US stores.",
    keywords: productName
      ? `${productName}, buy online, US shipping, international delivery, US2TJK`
      : "product details, US shopping, international shipping, US2TJK",
    canonical: productId
      ? `https://us2tjk.com/product/${productId}`
      : "https://us2tjk.com/product",
  }),

  auth: (
    type: "login" | "register" | "forgot-password" | "reset-password",
  ): MetaTagConfig => {
    const titles = {
      login: "Sign In - US2TJK",
      register: "Create Account - US2TJK",
      "forgot-password": "Reset Password - US2TJK",
      "reset-password": "Set New Password - US2TJK",
    };

    const descriptions = {
      login:
        "Sign in to your US2TJK account to start shopping from US stores.",
      register:
        "Create your US2TJK account and start shopping from US stores with international shipping.",
      "forgot-password":
        "Reset your US2TJK account password to regain access.",
      "reset-password": "Set a new password for your US2TJK account.",
    };

    return {
      title: titles[type],
      description: descriptions[type],
      keywords:
        "US2TJK account, sign in, register, password reset, user account",
      canonical: `https://us2tjk.com/${type === "login" ? "auth" : type}`,
    };
  },

  cart: (): MetaTagConfig => ({
    title: "Shopping Cart - US2TJK",
    description:
      "Review your selected items and proceed to checkout. Get your US purchases shipped internationally.",
    keywords:
      "shopping cart, checkout, US products, international shipping, US2TJK",
    canonical: "https://us2tjk.com/cart",
  }),

  checkout: (): MetaTagConfig => ({
    title: "Checkout - US2TJK",
    description:
      "Complete your order and get your US purchases shipped to your country.",
    keywords: "checkout, order, payment, shipping, delivery, US2TJK",
    canonical: "https://us2tjk.com/checkout",
  }),
};
